import { downloadData, getUrl } from 'aws-amplify/storage'
import { S3_STORAGE_SCOPE, resolveS3StorageTarget } from '@/config/portalEnvironment'
import { isNoLogoSelected, YANDLE_MARK_PUBLIC_PATH } from '@/utils/orgLogo'

const STORAGE_READ_OPTIONS = {
  expiresIn: 3600,
  validateObjectExistence: false,
}

const { bucket: logoBucket, region: logoRegion } = resolveS3StorageTarget(
  S3_STORAGE_SCOPE.GENERAL,
)
const LOGO_BUCKET_OPTIONS = {
  bucket: {
    bucketName: logoBucket,
    region: logoRegion,
  },
}

const MIME_BY_EXT = {
  png: 'image/png',
  jpg: 'image/jpeg',
  jpeg: 'image/jpeg',
  webp: 'image/webp',
  svg: 'image/svg+xml',
}

function inferMimeFromS3Key(key) {
  const lower = String(key || '').toLowerCase()
  const ext = lower.split('.').pop()
  return MIME_BY_EXT[ext] || 'application/octet-stream'
}

function isDirectUrl(path) {
  return /^https?:\/\//i.test(path) || /^data:/i.test(path) || /^blob:/i.test(path)
}

/**
 * Loads an org logo from S3 via Amplify Storage.
 * Tries getUrl first, then downloadData + blob URL.
 *
 * @param {string} s3Key
 * @returns {Promise<{ url: string|null, isBlob: boolean }>}
 */
export async function loadOrgLogoPreviewUrl(s3Key) {
  const path = String(s3Key ?? '').trim()
  if (!path) return { url: null, isBlob: false }

  if (isNoLogoSelected(path)) {
    return { url: YANDLE_MARK_PUBLIC_PATH, isBlob: false }
  }

  if (isDirectUrl(path)) {
    return { url: path, isBlob: false }
  }

  try {
    const { url } = await getUrl({
      path,
      options: {
        ...STORAGE_READ_OPTIONS,
        ...LOGO_BUCKET_OPTIONS,
      },
    })
    const href = url?.toString?.() ?? String(url || '')
    if (href) {
      return { url: href, isBlob: false }
    }
  } catch (err) {
    console.log('Amplify getUrl for org logo failed, trying downloadData', err)
  }

  try {
    const { body } = await downloadData({
      path,
      options: {
        ...STORAGE_READ_OPTIONS,
        ...LOGO_BUCKET_OPTIONS,
      },
    }).result
    const blob = await body.blob()
    if (blob?.size > 0) {
      const mime = blob.type || inferMimeFromS3Key(path)
      const buffer = await blob.arrayBuffer()
      const typed = new Blob([buffer], { type: mime })
      return { url: URL.createObjectURL(typed), isBlob: true }
    }
  } catch (err) {
    console.log('Amplify downloadData for org logo failed', err)
  }

  return { url: null, isBlob: false }
}

export function revokeOrgLogoPreviewUrl(url, isBlob) {
  if (isBlob && url && typeof url === 'string' && url.startsWith('blob:')) {
    URL.revokeObjectURL(url)
  }
}
